import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Package, Eye, Check, X, ShoppingCart, Phone, Mail, FileText, Building, User, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { formatCurrency, fmtNum } from '@/utils/currency';
import CurrencySymbol from '@/components/CurrencySymbol';
import { useNavigate } from 'react-router-dom';
import { SearchFilter } from '@/components/SearchFilter';

export default function Orders() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pos'); // 'pos' or 'customer'
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredCustomerOrders, setFilteredCustomerOrders] = useState([]);
  const [posOrdersCount, setPosOrdersCount] = useState(0);
  const [rfqOrdersCount, setRfqOrdersCount] = useState(0);

  // Helper to display price with proper currency symbol
  const PriceDisplay = ({ amount, currency = 'USD' }) => {
    const formattedAmount = fmtNum(amount || 0);
    console.log('PriceDisplay:', { amount, currency, formattedAmount });
    
    if (currency === 'MVR') {
      return (
        <span>
          <CurrencySymbol currency="MVR" /> {formattedAmount}
        </span>
      );
    }
    
    const symbol = currency === 'CHN' ? '¥' : '$';
    return <span>{symbol}{formattedAmount}</span>;
  };
  const [customerOrders, setCustomerOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomerOrder, setSelectedCustomerOrder] = useState(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showCustomerOrderDialog, setShowCustomerOrderDialog] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [poDocumentUrl, setPoDocumentUrl] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [customerFilterStatus, setCustomerFilterStatus] = useState('all');

  useEffect(() => {
    fetchOrders();
    fetchCustomerOrders();
  }, [filterStatus, customerFilterStatus]);

  const fetchOrders = async () => {
    try {
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      // Sort orders: new/processing first, then completed/cancelled
      const sorted = response.data.sort((a, b) => {
        const statusPriority = { 'new': 1, 'processing': 2, 'completed': 3, 'cancelled': 4 };
        return (statusPriority[a.status] || 5) - (statusPriority[b.status] || 5);
      });
      
      setOrders(sorted);
      setFilteredOrders(sorted);
      
      // Update POS orders count (new + processing)
      const pendingPosCount = sorted.filter(o => o.status === 'new' || o.status === 'processing').length;
      setPosOrdersCount(pendingPosCount);
    } catch (error) {
      toast.error('Failed to load orders');
    }
  };

  const viewOrder = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(response.data);
      setShowOrderDialog(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order status updated');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const openInPOS = async (orderId) => {
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/convert-to-sale`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Navigate to POS with order data
      navigate('/pos', { state: { orderData: response.data.order } });
    } catch (error) {
      toast.error('Failed to open order in POS');
    }
  };

  // Customer Orders Functions
  const fetchCustomerOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/customer-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter by status if needed
      let filtered = response.data;
      if (customerFilterStatus !== 'all') {
        filtered = response.data.filter(o => o.status === customerFilterStatus);
      }
      
      setCustomerOrders(filtered);
      setFilteredCustomerOrders(filtered);
      
      // Update RFQ orders count (Open + Processing)
      const pendingRfqCount = response.data.filter(o => 
        o.status === 'Open' || o.status === 'Processing'
      ).length;
      setRfqOrdersCount(pendingRfqCount);
    } catch (error) {
      toast.error('Failed to load customer orders');
    }
  };

  const viewCustomerOrder = (order) => {
    setSelectedCustomerOrder(order);
    setShowCustomerOrderDialog(true);
  };

  const updateCustomerOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await axios.put(
        `${API}/admin/customer-orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.invoice_created) {
        toast.success(`Order status updated to ${newStatus}. Invoice created automatically!`);
      } else {
        toast.success(`Order status updated to ${newStatus}`);
      }
      
      fetchCustomerOrders();
      if (selectedCustomerOrder?.id === orderId) {
        setSelectedCustomerOrder({ ...selectedCustomerOrder, status: newStatus });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    }
  };

  const verifyCustomerOrderPO = async (orderId) => {
    try {
      const response = await axios.put(
        `${API}/admin/customer-orders/${orderId}/verify-po`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(response.data.message || 'PO verified and invoice created successfully');
      fetchCustomerOrders();
      if (selectedCustomerOrder?.id === orderId) {
        setSelectedCustomerOrder({ 
          ...selectedCustomerOrder, 
          po_verification_status: 'Verified' 
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to verify PO';
      toast.error(errorMsg);
      console.error(error);
    }
  };

  const viewPODocument = (poUrl) => {
    setPoDocumentUrl(poUrl);
    setShowPODialog(true);
  };

  const getCustomerOrderStatusBadge = (status) => {
    const variants = {
      'Open': 'bg-blue-100 text-blue-800',
      'Processing': 'bg-yellow-100 text-yellow-800',
      'Consolidating': 'bg-purple-100 text-purple-800',
      'Dispatched': 'bg-orange-100 text-orange-800',
      'Delivered': 'bg-green-100 text-green-800'
    };
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'bg-blue-100 text-blue-800',
      processing: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Orders</h1>
            <p className="text-slate-600 mt-1">Manage all orders</p>
          </div>
        </div>

        {/* Main Tabs - POS Orders vs Customer RFQ Orders */}
        <div className="border-b border-slate-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('pos')}
              className={`pb-4 px-2 font-medium border-b-2 transition-colors relative ${
                activeTab === 'pos'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <ShoppingCart className="w-4 h-4 inline mr-2" />
              POS Orders
              {posOrdersCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {posOrdersCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('customer')}
              className={`pb-4 px-2 font-medium border-b-2 transition-colors relative ${
                activeTab === 'customer'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Customer RFQ Orders
              {rfqOrdersCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {rfqOrdersCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* POS Orders Tab Content */}
        {activeTab === 'pos' && (
          <>
            {/* Filter Tabs */}
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
              >
                All Orders
              </Button>
              <Button
                variant={filterStatus === 'new' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('new')}
              >
                New
              </Button>
              <Button
                variant={filterStatus === 'processing' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('processing')}
              >
                Processing
              </Button>
              <Button
                variant={filterStatus === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('completed')}
              >
                Completed
              </Button>
              <Button
                variant={filterStatus === 'cancelled' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('cancelled')}
              >
                Cancelled
              </Button>
            </div>

            {/* Search Filter for POS Orders */}
            <SearchFilter
              data={orders}
              onFilter={setFilteredOrders}
              searchFields={['order_number', 'customer_name', 'customer_email']}
              placeholder="Search by order #, customer name..."
            />

            {/* POS Orders List */}
            <div className="grid gap-4">
          {filteredOrders.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600">No orders found</p>
            </Card>
          ) : (
            filteredOrders.map(order => (
              <Card key={order.id} className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{order.order_number}</h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        {order.items.length} items
                      </p>
                      <p className="font-semibold text-slate-900">
                        {order.customer_name}
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {order.customer_phone}
                      </p>
                      {order.customer_email && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          {order.customer_email}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="text-right space-y-3">
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(order.total, order.currency)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewOrder(order.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {order.status === 'new' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => updateOrderStatus(order.id, 'processing')}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => updateOrderStatus(order.id, 'cancelled')}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {order.status === 'processing' && (
                        <Button
                          size="sm"
                          onClick={() => openInPOS(order.id)}
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Open in POS
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
            </div>
          </>
        )}

        {/* Customer RFQ Orders Tab Content */}
        {activeTab === 'customer' && (
          <>
            {/* Customer Order Filter Tabs */}
            <div className="flex gap-2">
              <Button
                variant={customerFilterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('all')}
              >
                All Orders
              </Button>
              <Button
                variant={customerFilterStatus === 'Open' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('Open')}
              >
                Open
              </Button>
              <Button
                variant={customerFilterStatus === 'Processing' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('Processing')}
              >
                Processing
              </Button>
              <Button
                variant={customerFilterStatus === 'Consolidating' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('Consolidating')}
              >
                Consolidating
              </Button>
              <Button
                variant={customerFilterStatus === 'Dispatched' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('Dispatched')}
              >
                Dispatched
              </Button>
              <Button
                variant={customerFilterStatus === 'Delivered' ? 'default' : 'outline'}
                onClick={() => setCustomerFilterStatus('Delivered')}
              >
                Delivered
              </Button>
            </div>

            {/* Search Filter for Customer Orders */}
            <SearchFilter
              data={customerOrders}
              onFilter={setFilteredCustomerOrders}
              searchFields={['order_number', 'customer_name', 'customer_email', 'po_number']}
              placeholder="Search by order #, customer name, PO number..."
            />

            {/* Customer Orders List */}
            <div className="grid gap-4">
              {filteredCustomerOrders.length === 0 ? (
                <Card className="p-12 text-center">
                  <FileText className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600">No customer orders found</p>
                </Card>
              ) : (
                filteredCustomerOrders.map(order => (
                  <Card key={order.id} className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{order.order_number}</h3>
                          {getCustomerOrderStatusBadge(order.status)}
                          {(!order.po_number || !order.po_document_url) && (
                            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded font-medium">
                              <AlertCircle className="w-3 h-3" />
                              PO Missing - Cannot Process
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 text-sm text-slate-600">
                          <p className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span className="font-semibold text-slate-900">{order.customer_name}</span>
                          </p>
                          <p className="flex items-center gap-2">
                            <Building className="w-4 h-4" />
                            {order.business_name}
                          </p>
                          <p className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            {order.customer_email}
                          </p>
                          <p className="flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            {order.items?.length || 0} items • <PriceDisplay amount={order.total} currency={order.currency} />
                          </p>
                          <p className="text-xs text-slate-500">
                            Created: {new Date(order.created_at).toLocaleString()}
                          </p>
                        </div>

                        {/* PO Status Section */}
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-700 mb-2">Purchase Order Status:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <div className={`flex items-center gap-2 text-sm ${order.po_number ? 'text-green-600' : 'text-red-600'}`}>
                              {order.po_number ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  <span>PO #: {order.po_number}</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4" />
                                  <span>PO Number: Not Provided</span>
                                </>
                              )}
                            </div>
                            <div className={`flex items-center gap-2 text-sm ${order.po_document_url ? 'text-green-600' : 'text-red-600'}`}>
                              {order.po_document_url ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  <span>PO Document: Uploaded</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 ml-2"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      viewPODocument(order.po_document_url);
                                    }}
                                  >
                                    <FileText className="w-3 h-3 mr-1" />
                                    View
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-4 h-4" />
                                  <span>PO Document: Not Uploaded</span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* PO Verification Status for Tier 1 */}
                          {order.requires_immediate_invoice && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-2">
                                {order.po_verification_status === 'Verified' ? (
                                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded font-medium">
                                    <CheckCircle className="w-3 h-3" />
                                    PO Verified - Invoice Created
                                  </span>
                                ) : order.po_verification_status === 'Pending' && order.po_document_url ? (
                                  <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded font-medium">
                                    <AlertCircle className="w-3 h-3" />
                                    Awaiting PO Verification
                                  </span>
                                ) : null}
                              </div>
                              
                              {/* Payment Verification Status */}
                              {order.invoice_id && order.po_verification_status === 'Verified' && (
                                <div className="flex items-center gap-2">
                                  {order.payment_verified === true ? (
                                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded font-medium">
                                      <CheckCircle className="w-3 h-3" />
                                      Payment Verified - Ready to Process
                                    </span>
                                  ) : order.payment_proof_uploaded ? (
                                    <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded font-medium">
                                      <AlertCircle className="w-3 h-3" />
                                      Payment Proof Uploaded - Awaiting Verification
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded font-medium">
                                      <AlertCircle className="w-3 h-3" />
                                      Awaiting Payment from Customer
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Warning if payment not verified */}
                              {order.invoice_id && !order.payment_verified && order.status === 'Open' && (
                                <p className="text-xs text-red-600 font-medium">
                                  ⚠️ Payment must be verified before order can be processed
                                </p>
                              )}
                            </div>
                          )}
                          
                          {(!order.po_number || !order.po_document_url) && !order.requires_immediate_invoice && (
                            <p className="text-xs text-red-600 mt-2 font-medium">
                              ⚠️ Cannot process order until customer provides PO details
                            </p>
                          )}
                          {(!order.po_number || !order.po_document_url) && order.requires_immediate_invoice && (
                            <p className="text-xs text-red-600 mt-2 font-medium">
                              ⚠️ Waiting for customer to provide PO details
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="text-right space-y-3">
                        <p className="text-2xl font-bold text-slate-900">
                          <PriceDisplay amount={order.total} currency={order.currency} />
                        </p>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewCustomerOrder(order)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          
                          {/* PO Verification Button for Tier 1 Customers */}
                          {order.requires_immediate_invoice && 
                           order.po_document_url && 
                           order.po_verification_status === 'Pending' && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => verifyCustomerOrderPO(order.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Verify PO & Create Invoice
                            </Button>
                          )}
                          
                          {/* Status Update Buttons - Only show if PO is verified for Tier 1 */}
                          {order.status === 'Open' && (!order.requires_immediate_invoice || order.po_verification_status === 'Verified') && (
                            <Button
                              size="sm"
                              onClick={() => updateCustomerOrderStatus(order.id, 'Processing')}
                              disabled={order.requires_immediate_invoice && (!order.invoice_id || !order.payment_verified)}
                              title={order.requires_immediate_invoice && !order.payment_verified ? 'Payment must be verified first' : ''}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Start Processing
                            </Button>
                          )}
                          {order.status === 'Processing' && (
                            <Button
                              size="sm"
                              onClick={() => updateCustomerOrderStatus(order.id, 'Consolidating')}
                            >
                              Ready to Consolidate
                            </Button>
                          )}
                          {order.status === 'Consolidating' && (
                            <Button
                              size="sm"
                              onClick={() => updateCustomerOrderStatus(order.id, 'Dispatched')}
                            >
                              Mark Dispatched
                            </Button>
                          )}
                          {order.status === 'Dispatched' && (
                            <Button
                              size="sm"
                              onClick={() => updateCustomerOrderStatus(order.id, 'Delivered')}
                            >
                              Mark Delivered
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Customer Information</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {selectedOrder.customer_name}</p>
                  <p><strong>Phone:</strong> {selectedOrder.customer_phone}</p>
                  {selectedOrder.customer_email && (
                    <p><strong>Email:</strong> {selectedOrder.customer_email}</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Order Items</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between p-3 bg-slate-50 rounded">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-600">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold">{formatCurrency(item.total, selectedOrder?.currency)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(selectedOrder.subtotal, selectedOrder?.currency)}</span>
                </div>
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>Discount:</span>
                    <span>-{formatCurrency(selectedOrder.discount, selectedOrder?.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(selectedOrder.total, selectedOrder?.currency)}</span>
                </div>
              </div>

              {selectedOrder.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-slate-600">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="flex gap-2">
                {selectedOrder.status === 'new' && (
                  <>
                    <Button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'processing');
                        setShowOrderDialog(false);
                      }}
                    >
                      Accept Order
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, 'cancelled');
                        setShowOrderDialog(false);
                      }}
                    >
                      Cancel Order
                    </Button>
                  </>
                )}
                {selectedOrder.status === 'processing' && (
                  <Button onClick={() => openInPOS(selectedOrder.id)}>
                    Open in POS
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Order Details Dialog */}
      <Dialog open={showCustomerOrderDialog} onOpenChange={setShowCustomerOrderDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer RFQ Order Details - {selectedCustomerOrder?.order_number}</DialogTitle>
          </DialogHeader>

          {selectedCustomerOrder && (
            <div className="space-y-6">
              {/* PO Status Alert - Top Priority */}
              {(!selectedCustomerOrder.po_number || !selectedCustomerOrder.po_document_url) && (
                <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-bold text-red-900 text-lg mb-2">⚠️ Cannot Process Order</h3>
                      <p className="text-red-700 mb-3">Customer has not provided required PO details:</p>
                      <ul className="space-y-1 text-sm text-red-700 ml-4">
                        {!selectedCustomerOrder.po_number && (
                          <li className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            PO Number missing
                          </li>
                        )}
                        {!selectedCustomerOrder.po_document_url && (
                          <li className="flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            PO Document not uploaded
                          </li>
                        )}
                      </ul>
                      <p className="text-sm text-red-600 mt-3 font-medium">
                        Please contact customer to provide PO details before processing this order.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* PO Details Section - If Complete */}
              {selectedCustomerOrder.po_number && selectedCustomerOrder.po_document_url && (
                <div className="bg-green-50 border-2 border-green-300 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-bold text-green-900 text-lg mb-2">✓ PO Details Complete</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-green-700 mb-1">PO Number:</p>
                          <p className="text-lg font-bold text-green-900">{selectedCustomerOrder.po_number}</p>
                        </div>
                        <div>
                          <p className="text-sm text-green-700 mb-1">PO Document:</p>
                          <Button
                            size="sm"
                            onClick={() => viewPODocument(selectedCustomerOrder.po_document_url)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View PO Document
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">{/* rest of the content */}
                <div>
                  <h3 className="font-semibold mb-2">Customer Information</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Name:</strong> {selectedCustomerOrder.customer_name}</p>
                    <p><strong>Company:</strong> {selectedCustomerOrder.business_name}</p>
                    <p><strong>Email:</strong> {selectedCustomerOrder.customer_email}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Order Information</h3>
                  <div className="space-y-1 text-sm">
                    <p><strong>Order Number:</strong> {selectedCustomerOrder.order_number}</p>
                    <p><strong>Status:</strong> {getCustomerOrderStatusBadge(selectedCustomerOrder.status)}</p>
                    <p><strong>Total Amount:</strong> <PriceDisplay amount={selectedCustomerOrder.total} currency={selectedCustomerOrder.currency} /></p>
                    <p><strong>Created:</strong> {new Date(selectedCustomerOrder.created_at).toLocaleString()}</p>
                    {selectedCustomerOrder.updated_at && (
                      <p><strong>Last Updated:</strong> {new Date(selectedCustomerOrder.updated_at).toLocaleString()}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              {selectedCustomerOrder.order_delivery_details && (
                <div>
                  <h3 className="font-semibold mb-2">Delivery Details</h3>
                  <div className="space-y-1 text-sm bg-slate-50 p-3 rounded">
                    <p><strong>Method:</strong> {selectedCustomerOrder.order_delivery_details.method === 'landmark' ? 'Landmark' : 'Supply Boat'}</p>
                    {selectedCustomerOrder.order_delivery_details.method === 'landmark' ? (
                      <>
                        <p><strong>Contact Person:</strong> {selectedCustomerOrder.order_delivery_details.first_name} {selectedCustomerOrder.order_delivery_details.last_name}</p>
                        <p><strong>Contact Number:</strong> {selectedCustomerOrder.order_delivery_details.contact_number}</p>
                        <p><strong>Designation:</strong> {selectedCustomerOrder.order_delivery_details.designation}</p>
                        {selectedCustomerOrder.order_delivery_details.google_map_link && (
                          <p><strong>Map Link:</strong> <a href={selectedCustomerOrder.order_delivery_details.google_map_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View Location</a></p>
                        )}
                      </>
                    ) : (
                      <>
                        <p><strong>Captain:</strong> {selectedCustomerOrder.order_delivery_details.captain_full_name}</p>
                        <p><strong>Captain ID:</strong> {selectedCustomerOrder.order_delivery_details.captain_id_no}</p>
                        <p><strong>Captain Number:</strong> {selectedCustomerOrder.order_delivery_details.captain_number}</p>
                        <p><strong>Dhoni Name:</strong> {selectedCustomerOrder.order_delivery_details.dhoni_name}</p>
                        <p><strong>Jetty:</strong> {selectedCustomerOrder.order_delivery_details.jetty}</p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Order Items</h3>
                <div className="space-y-2">
                  {selectedCustomerOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between p-3 bg-slate-50 rounded">
                      <div className="flex-1">
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-slate-600">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold"><PriceDisplay amount={item.price} currency={item.currency} /> each</p>
                        <p className="text-sm text-slate-600">Total: <PriceDisplay amount={item.price * item.quantity} currency={item.currency} /></p>
                      </div>
                    </div>
                  )) || (
                    <p className="text-slate-500 italic">No items</p>
                  )}
                </div>
              </div>

              {selectedCustomerOrder.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{selectedCustomerOrder.notes}</p>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                {selectedCustomerOrder.status === 'Open' && (
                  <Button
                    onClick={() => {
                      updateCustomerOrderStatus(selectedCustomerOrder.id, 'Processing');
                      setShowCustomerOrderDialog(false);
                    }}
                    disabled={!selectedCustomerOrder.po_number || !selectedCustomerOrder.po_document_url}
                    className={!selectedCustomerOrder.po_number || !selectedCustomerOrder.po_document_url ? 'cursor-not-allowed' : ''}
                  >
                    Start Processing
                  </Button>
                )}
                {selectedCustomerOrder.status === 'Processing' && (
                  <Button
                    onClick={() => {
                      updateCustomerOrderStatus(selectedCustomerOrder.id, 'Consolidating');
                      setShowCustomerOrderDialog(false);
                    }}
                  >
                    Ready to Consolidate
                  </Button>
                )}
                {selectedCustomerOrder.status === 'Consolidating' && (
                  <Button
                    onClick={() => {
                      updateCustomerOrderStatus(selectedCustomerOrder.id, 'Dispatched');
                      setShowCustomerOrderDialog(false);
                    }}
                  >
                    Mark as Dispatched
                  </Button>
                )}
                {selectedCustomerOrder.status === 'Dispatched' && (
                  <Button
                    onClick={() => {
                      updateCustomerOrderStatus(selectedCustomerOrder.id, 'Delivered');
                      setShowCustomerOrderDialog(false);
                    }}
                  >
                    Mark as Delivered
                  </Button>
                )}
                {(!selectedCustomerOrder.po_number || !selectedCustomerOrder.po_document_url) && (
                  <p className="text-sm text-red-600 flex items-center gap-2 ml-4">
                    <AlertCircle className="w-4 h-4" />
                    Cannot update status until PO details are provided
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PO Document Viewer Dialog */}
      <Dialog open={showPODialog} onOpenChange={setShowPODialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Purchase Order Document</DialogTitle>
          </DialogHeader>
          <div className="w-full h-[70vh] overflow-auto">
            {poDocumentUrl ? (
              poDocumentUrl.startsWith('data:application/pdf') ? (
                <iframe
                  src={poDocumentUrl}
                  className="w-full h-full border-0"
                  title="PO Document"
                />
              ) : (
                <img
                  src={poDocumentUrl}
                  alt="PO Document"
                  className="w-full h-auto"
                />
              )
            ) : (
              <p className="text-center text-gray-500 py-8">No document to display</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
